// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL CHAT RÁPIDO DEL BUSCA (B-875, 26-sep-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «pudiendo entablar un chat desde ahi mismo (vinculado al chat de equipo, pero que sea rápido y no requiera
//  ir al chat de equipo)». Es el MISMO chat directo de las dos personas (/api/chat), así que lo que se dice aquí está
//  luego en el chat de equipo, y al revés. Los últimos mensajes, que se refrescan solos mientras está abierto, y la caja
//  de escribir del chat (components/chat/ComposerChat): texto y notas de voz, que el chat transcribe al subirlas.
import { useCallback, useEffect, useRef, useState } from 'react'
import api from '../../services/api'
import { useToast } from '../../context/ToastContext'
import { msgError } from '../../utils/errores'
import ComposerChat from '../chat/ComposerChat'
import { t as i18nT } from '../../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

const CADA_MS = 4000
// B-914 · LA HORA DEL MENSAJE, LA QUE DA EL SERVIDOR. El chat no manda `created_at` (manda `hora`, `fecha` y `dia`, ya
// escritos por la base), y `new Date(undefined)` pintaba «Invalid Date» debajo de cada mensaje. La de hoy, solo la hora;
// la de otro día, con su día.
const hoyLocal = () => { const d = new Date(), p = (n) => String(n).padStart(2, '0'); return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) }
export function horaDe(m, hoy = hoyLocal()) {
  if (!m) return ''
  if (m.dia && m.dia !== hoy) return m.fecha || m.hora || ''
  return m.hora || m.fecha || ''
}

// Lo que se enseña de un mensaje: su texto, o de una nota de voz lo que dice.
export function textoDeMensaje(m) {
  if (!m) return ''
  if (m.texto && String(m.texto).trim()) return String(m.texto)
  if (m.tipo === 'audio') return m.transcripcion ? i18nT('components.busca.ChatRapido.nota_de_voz', 'Nota de voz: {{transcripcion}}', { transcripcion: m.transcripcion }) : i18nT('components.busca.ChatRapido.nota_de_voz_2', 'Nota de voz')
  if (m.adjunto_nombre) return i18nT('components.busca.ChatRapido.archivo', 'Archivo: {{adjunto_nombre}}', { adjunto_nombre: m.adjunto_nombre })
  return ''
}

export default function ChatRapido({ canalId, yoId, alto = 220 }) {
  const toast = useToast()
  const [mensajes, setMensajes] = useState([])
  const [texto, setTexto] = useState('')
  const listaRef = useRef(null)
  const cargar = useCallback(async () => {
    if (!canalId) return
    try {
      const r = await api.get('/chat/canales/' + canalId + '/mensajes')
      setMensajes(Array.isArray(r.data) ? r.data.slice(-30) : [])
    } catch { }
  }, [canalId])
  useEffect(() => {
    cargar()
    const t = setInterval(cargar, CADA_MS)
    return () => clearInterval(t)
  }, [cargar])
  useEffect(() => { const el = listaRef.current; if (el) el.scrollTop = el.scrollHeight }, [mensajes.length])

  const enviar = async () => {
    const t = texto.trim()
    if (!t || !canalId) return
    try { await api.post('/chat/canales/' + canalId + '/mensajes', { texto: t }); setTexto(''); cargar() }
    catch (e) { toast.error(msgError(e, i18nT('components.busca.ChatRapido.no_se_pudo_enviar', 'No se pudo enviar'))) }
  }
  const adjuntar = async (file) => {
    if (!file || !canalId) return
    const fd = new FormData()
    fd.append('archivo', file)
    try { await api.post('/chat/canales/' + canalId + '/adjunto', fd); cargar() }
    catch (e) { toast.error(msgError(e, i18nT('components.busca.ChatRapido.no_se_pudo_enviar_el', 'No se pudo enviar el archivo'))) }
  }
  const enviarAudio = async (blob) => {
    if (!blob || !blob.size) return
    const tipo = blob.type || 'audio/webm'
    await adjuntar(new File([blob], 'nota-de-voz.' + (tipo.includes('mp4') || tipo.includes('m4a') ? 'm4a' : 'webm'), { type: tipo }))
  }

  return (
    <div data-chat-rapido style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div ref={listaRef} style={{ maxHeight: alto, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 2px' }}>
        {mensajes.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{i18nT('components.busca.ChatRapido.todavia_no_os_habeis_escrito', 'Todavía no os habéis escrito.')}</span>}
        {mensajes.map(m => {
          const mio = yoId != null && Number(m.agente_id) === Number(yoId)
          const t = textoDeMensaje(m)
          if (!t) return null
          return (
            <div key={m.id} data-mensaje-rapido={m.id} style={{ alignSelf: mio ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '6px 10px', borderRadius: 12,
              background: mio ? 'color-mix(in srgb, var(--primary) 14%, var(--surface))' : 'var(--surface-2)', fontSize: 13, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
              {t}
              <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2, textAlign: 'right' }}>{horaDe(m)}</span>
            </div>
          )
        })}
      </div>
      <ComposerChat valor={texto} setValor={setTexto} onEnviar={enviar} onAdjuntar={adjuntar} onAudio={enviarAudio} />
    </div>
  )
}
